import { throwIfAborted } from '../sync/requestDeadline'
/**
 * keyManager.ts — Key hierarchy and session management for Superelectric Ink E2EE.
 *
 * Key derivation chain (Standard Notes-inspired):
 *
 *   Password + Salt = SHA256(username + ":" + server-issued kdf_nonce)
 *        │
 *        ▼  Argon2id (64 MB / 3 iter / p=4)
 *   512-bit Root Key (64 bytes)
 *        │
 *        ├─ HKDF('superelectric-ink-auth-key-v1')   → 32-byte Auth Key
 *        │       Sent to server; server SHA-256-hashes it for storage.
 *        │       Password never leaves the client.
 *        │
 *        ├─ HKDF('superelectric-ink-master-key-v1') → 32-byte Master Encryption Key (MEK)
 *        │       Wraps/unwraps per-document keys.
 *        │       Never sent to the server.
 *        │
 *        └─ HKDF('superelectric-ink-kek-v1')        → 32-byte Key Encryption Key (KEK)
 *                Encrypts the MEK itself for server-side backup.
 *                Allows the MEK to be recovered on a new device from the server-stored
 *                encrypted blob, without the server ever seeing the plaintext MEK.
 *
 * The `username` is the app-internal login identifier (NOT an email address — a
 * separate, inert `email` field exists purely for future account communication and
 * plays no role in key derivation). Because the username doubles as salt material,
 * the actual per-account salt also mixes in a random `kdf_nonce` fetched from the
 * server via GET /auth/params BEFORE Argon2id runs. This closes off offline bulk
 * precomputation attacks that a public identifier alone (like an email) would allow:
 * computing the real salt for any account now requires querying the server first.
 *
 * Document key hierarchy:
 *   Each document has a unique Document Encryption Key (DEK).
 *   The DEK is encrypted with the MEK (or with the recipient's public key for sharing).
 *   Stored as an `EncryptedBlob` in D1 (document_keys table).
 *
 * Session lifecycle:
 *   1. `buildRegistrationPayload(password, username, kdfNonce)` — generates all key
 *      material for a new account; returns a payload ready to POST to /auth/register.
 *   2. `unlockSession(password, username, kdfNonce, serverPayload)` — re-derives the
 *      password KEK and decrypts the server-stored wrapped MEK.
 *      After this call the manager holds the MEK in memory.
 *   3. `clear()` — zeros/drops all in-memory key material (called on logout).
 *
 * ARCHITECTURAL REQUIREMENT FOR PASSWORD ROTATION:
 *   When password rotation is implemented, it MUST re-wrap the EXISTING MEK under
 *   a new KEK (rather than generating a new MEK). This ensures existing local key
 *   caches across the user's devices remain valid and uninterrupted.
 *
 * IMPORTANT: All private key material is non-extractable after import into memory.
 * The only time a private key is extractable is during the registration payload build (so we can wrap it).
 */

import { argon2id } from 'hash-wasm'
import {
  type EncryptedBlob,
  toBase64Url,
  fromBase64Url,
  hkdfDerive,
  aesGcmEncrypt,
  aesGcmDecrypt,
  generateAesKey,
  exportAesKey,
  importAesKey,
  sha256Bytes,
  bytesToBase32,
  base32ToBytes,
  formatRecoveryCode,
  unformatRecoveryCode,
} from './crypto'
import { secureStorage } from './secureStorage'
import { getPlatformBridge } from '../platform/platformBridge'
import type { CryptoWorkerMessage, CryptoWorkerResponse } from '../../workers/cryptoWorker'

// ── Constants ─────────────────────────────────────────────────────────────────

/** HKDF context strings — changing these is a breaking change. */
const INFO_AUTH_KEY = 'superelectric-ink-auth-key-v1'
const INFO_MASTER_KEY = 'superelectric-ink-master-key-v1'
const INFO_KEK = 'superelectric-ink-kek-v1'
const INFO_RECOVERY_AUTH_KEY = 'superelectric-ink-recovery-auth-key-v1'
const INFO_RECOVERY_KEK = 'superelectric-ink-recovery-kek-v1'

/**
 * Length in bytes of the random recovery code secret (160 bits). This is
 * already high-entropy randomness — unlike a password, it does NOT need to
 * go through Argon2id; it's fed straight into HKDF (see deriveRecoveryKeySet).
 */
const RECOVERY_CODE_BYTES = 20

/** Schema version of the persisted keystore. */
const KEY_STORE_SCHEMA_VERSION = 1

/** Fixed canary plaintext used for integrity check during warm key restore. */
const KEY_CANARY_PLAINTEXT = 'sei-key-canary-v1'

/**
 * Argon2id parameters.
 * These must NEVER be reduced without a migration strategy — doing so would allow
 * offline dictionary attacks on the derived auth key if the server DB is leaked.
 *
 * Memory: 64 MB, Iterations: 3, Parallelism: 4, Output: 64 bytes (512 bits)
 */
const ARGON2_PARAMS = {
  memorySize: 65536,  // 64 MB in KiB
  iterations: 3,
  parallelism: 4,
  hashLength: 64,     // 512-bit root key
  outputType: 'binary' as const,
}

// ── Crypto worker dispatch ───────────────────────────────────────────────────
//
// Argon2id is the ~1-3s slow step in deriveRootKey() below. Rather than run it
// inline on the calling (main UI) thread, it's dispatched to cryptoWorker.ts
// and awaited by correlating the response's `id` back to the request — the
// same id-based req/response shape cryptoWorker.ts already defines. A single
// worker instance is lazily spun up and reused for the lifetime of the tab.

let cryptoWorker: Worker | null = null
let cryptoWorkerRequestId = 0
const cryptoWorkerPending = new Map<
string,
{ resolve: (value: any) => void; reject: (err: Error) => void }
>()

function getCryptoWorker(): Worker | null {
  if (typeof window === 'undefined') return null
    if (!cryptoWorker) {
      try {
        cryptoWorker = new Worker(
          new URL('../../workers/cryptoWorker.ts', import.meta.url),
                                  { type: 'module' },
        )
        cryptoWorker.onmessage = (event: MessageEvent<CryptoWorkerResponse>) => {
          const { id, success, result, error } = event.data
          const pending = cryptoWorkerPending.get(id)
          if (!pending) return
            cryptoWorkerPending.delete(id)
            if (success) {
              pending.resolve(result)
            } else {
              pending.reject(new Error(error || 'Crypto worker operation failed'))
            }
        }
        cryptoWorker.onerror = (err) => {
          console.warn('[KeyManager] Crypto worker error:', err)
          // Fail every in-flight request rather than leaving callers hanging forever.
          for (const pending of cryptoWorkerPending.values()) {
            pending.reject(new Error('Crypto worker error'))
          }
          cryptoWorkerPending.clear()
        }
      } catch (e) {
        console.warn('[KeyManager] Failed to spawn crypto worker:', e)
        return null
      }
    }
    return cryptoWorker
}

/**
 * Dispatches `action` + `payload` to the crypto worker and resolves with
 * `result` once the response carrying the matching `id` comes back. Rejects
 * if a worker can't be spawned in this environment (e.g. no `window`) — the
 * caller (deriveRootKey) decides the fallback behavior.
 */
function dispatchToCryptoWorker<T>(action: CryptoWorkerMessage['action'], payload: any): Promise<T> {
  const worker = getCryptoWorker()
  if (!worker) {
    return Promise.reject(new Error('Crypto worker unavailable in this environment'))
  }
  const id = String(cryptoWorkerRequestId++)
  return new Promise<T>((resolve, reject) => {
    cryptoWorkerPending.set(id, { resolve, reject })
    worker.postMessage({ id, action, payload } as CryptoWorkerMessage)
  })
}

/**
 * Encrypts the constant canary string under the MEK for integrity checks.
 */
export async function generateKeyCanary(masterKey: CryptoKey): Promise<EncryptedBlob> {
  const canaryBytes = new TextEncoder().encode(KEY_CANARY_PLAINTEXT)
  return aesGcmEncrypt(masterKey, canaryBytes)
}

/**
 * Decrypts and verifies the canary blob against the MEK.
 */
export async function verifyKeyCanary(masterKey: CryptoKey, canary: EncryptedBlob): Promise<boolean> {
  try {
    const blob = normaliseBlob(canary)
    const decrypted = await aesGcmDecrypt(masterKey, blob)
    const str = new TextDecoder().decode(decrypted)
    return str === KEY_CANARY_PLAINTEXT
  } catch (err) {
    console.warn('[KeyManager] Canary integrity check failed:', err)
    return false
  }
}

/** Verify the full saved vault, including functional non-extractable DEK handles. */
// Native serde_json serializes object fields in a different order from JS.
// Compare all metadata values while ignoring JSON object property order.
function canonicalMetadata(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(canonicalMetadata)
  if (value && typeof value === 'object') {
    const record = value as Record<string, unknown>
    return Object.fromEntries(Object.keys(record).sort().map(key => [key, canonicalMetadata(record[key])]))
  }
  return value
}

export async function verifyKeyStoreWrite(data: any, saved: any, allowAdditionalDeks = false): Promise<void> {
  if (!saved || saved.accountId !== data.accountId || saved.canary?.ciphertext !== data.canary?.ciphertext || saved.canary?.iv !== data.canary?.iv) {
    throw new Error('Saved encryption keys failed verification')
  }
  const key = saved.mek instanceof CryptoKey ? saved.mek : await importAesKey(fromBase64Url(saved.mek), ['encrypt', 'decrypt'], false)
  if (!await verifyKeyCanary(key, saved.canary)) throw new Error('Saved encryption keys failed integrity verification')
  if (JSON.stringify(canonicalMetadata(saved.offlineUnlock)) !== JSON.stringify(canonicalMetadata(data.offlineUnlock))) throw new Error('Saved offline unlock metadata failed verification')
  if (saved.version !== data.version || saved.kdfNonce !== data.kdfNonce) throw new Error('Saved key metadata failed verification')
  const expectedDeks = data.dekCache ?? {}
  const actualDeks = saved.dekCache ?? {}
  if (!allowAdditionalDeks && Object.keys(expectedDeks).length !== Object.keys(actualDeks).length) throw new Error('Saved document keys are incomplete')
  for (const [id, entry] of Object.entries(expectedDeks)) {
    const expected = entry as { epoch: number; dek: string | CryptoKey }
    const actual = actualDeks[id]
    if (!actual || actual.epoch < expected.epoch || (!allowAdditionalDeks && actual.epoch !== expected.epoch)) throw new Error('Saved document keys are incomplete')
    if (allowAdditionalDeks && actual.epoch > expected.epoch) continue
    const expectedKey = expected.dek instanceof CryptoKey ? expected.dek : await importAesKey(fromBase64Url(expected.dek), ['encrypt', 'decrypt'], false)
    const actualKey = actual.dek instanceof CryptoKey ? actual.dek : await importAesKey(fromBase64Url(actual.dek), ['encrypt', 'decrypt'], false)
    const challenge = await generateKeyCanary(expectedKey)
    if (!await verifyKeyCanary(actualKey, challenge)) throw new Error('Saved document keys failed integrity verification')
  }
}

// ── Types ─────────────────────────────────────────────────────────────────────

/** Persisted DEK entry within the local keystore. */
interface PersistedDekEntry {
  epoch: number
  dek: string | CryptoKey
}

/**
 * Schema for persisted local key store (keystore.json on native, or object store on web).
 */
export interface PersistedKeyStore {
  version: number
  accountId?: string
  offlineUnlock?: { username: string; kdfNonce: string; encryptedMasterKey: EncryptedBlob | string }
  mek: string | CryptoKey
  dekCache?: Record<string, PersistedDekEntry>
  canary: EncryptedBlob
  /**
   * The cached KDF nonce used for local password derivation. Offline unlock
   * stores its own username/nonce/wrapped MEK so credential changes elsewhere
   * do not affect local unlock until this device successfully signs in again.
   */
  kdfNonce?: string
}

/** All the key material needed to create a new account on the server. */
interface RegistrationPayload {
  /**
   * The Argon2id-derived auth key encoded as base64url.
   * The server will SHA-256-hash this before storage — so the DB never holds
   * the raw derived key. The client never sends the password itself.
   */
  authKeyHash: string
  /** MEK encrypted with the KEK, for server-side backup / device recovery. */
  encryptedMasterKey: EncryptedBlob
  /** HKDF-derived auth key for the recovery code, same treatment as authKeyHash. */
  recoveryAuthKeyHash: string
  /** The SAME MEK, independently encrypted with a KEK derived from the recovery code. */
  recoveryWrappedMasterKey: EncryptedBlob
}

/** Result of buildRegistrationPayload — the payload to send, plus the one-time recovery code. */
export interface RegistrationResult {
  payload: RegistrationPayload
  /**
   * The plaintext, human-readable recovery code (formatted, dash-separated).
   * Shown to the user ONCE, at registration — never persisted anywhere on
   * the client, and never sent to the server (only its HKDF-derived hashes are).
   */
  recoveryCode: string
}

/** Key material needed to authorize and perform a password reset via recovery code. */
export interface PasswordResetPayload {
  authKeyHash: string
  encryptedMasterKey: EncryptedBlob
}

/** Fresh recovery material for an existing MEK. The plaintext code is never persisted. */
export interface RecoveryRotationResult {
  recoveryCode: string
  recoveryAuthKeyHash: string
  recoveryWrappedMasterKey: EncryptedBlob
}

/** Opaque server-stored key blobs returned by /auth/login. */
export interface ServerKeyPayload {
  encryptedMasterKey: EncryptedBlob | string  // string = legacy compat (base64url iv+ct)
}

// ── Internal derived key set ──────────────────────────────────────────────────

interface KeySet {
  /** Raw 32-byte auth key (high-entropy, derived from Argon2id — not the password). */
  authKeyRaw: Uint8Array
  /** Imported AES-GCM CryptoKey for MEK operations. */
  masterKey: CryptoKey
  /** Imported AES-GCM CryptoKey for KEK operations (MEK backup encryption). */
  kek: CryptoKey
}

// ── KeyManager ────────────────────────────────────────────────────────────────

export class KeyManager {
  /** The Master Encryption Key. */
  private masterKey: CryptoKey | null = null
  private accountId: string | null = null
  private offlineUnlock: PersistedKeyStore['offlineUnlock']
  private persistencePaused = false
  private persistQueue: Promise<unknown> = Promise.resolve()

  /** Per-document DEKs cached by document ID. */
  private documentKeys: Map<string, CryptoKey> = new Map()

  /** Per-document DEK epoch versions. */
  private documentEpochs: Map<string, number> = new Map()

    /** Timer handle for debounced keystore writes. */
    private persistDebounceTimer: ReturnType<typeof setTimeout> | null = null

      /**
       * The kdf_nonce last used to derive the root key, kept so it can be
       * persisted alongside the MEK in the local keystore (see PersistedKeyStore).
       */
      private cachedKdfNonce: string | null = null

  /** Listeners notified whenever key session lock state changes. */
  private unlockListeners: Array<(unlocked: boolean) => void> = []

  /**
   * Schedules a debounced save of the keystore to local cache (500ms).
   */
  schedulePersist(delayMs = 500): void {
    if (this.persistDebounceTimer !== null) {
      clearTimeout(this.persistDebounceTimer)
    }
    this.persistDebounceTimer = setTimeout(() => {
      this.persistDebounceTimer = null
      this.saveToLocalCache().then(saved => {
        if (!saved && !this.persistencePaused && this.masterKey) window.dispatchEvent(new CustomEvent('auth:storage-error'))
      }).catch((err) => {
        console.warn('[KeyManager] Debounced keystore persist failed:', err)
      })
    }, delayMs)
  }

  /**
   * Subscribe to key unlock state changes.
   * Returns an unsubscribe callback.
   */
  onUnlockChange(listener: (unlocked: boolean) => void): () => void {
    this.unlockListeners.push(listener)
    try {
      listener(this.isUnlocked())
    } catch {}
    return () => {
      this.unlockListeners = this.unlockListeners.filter((l) => l !== listener)
    }
  }

  private notifyUnlockChange(): void {
    const unlocked = this.isUnlocked()
    for (const listener of this.unlockListeners) {
      try {
        listener(unlocked)
      } catch (err) {
        console.warn('[KeyManager] Error in unlock listener:', err)
      }
    }
  }

  // ── Argon2id ───────────────────────────────────────────────────────────────

  /**
   * Derives the 512-bit root key from `password`, `username`, and the
   * server-issued `kdfNonce` using Argon2id. This is the slowest step
   * (~1-3 seconds) — it runs once per login/register, off the main thread
   * via cryptoWorker.ts (falling back to an inline, main-thread call only if
   * a worker can't be spawned — see dispatchToCryptoWorker above).
   *
   * The salt is SHA256(username + ":" + kdfNonce) rather than the username
   * alone: the username is public-ish (an attacker who knows it can still try
   * to guess it), but the kdfNonce can only be obtained by querying the server
   * (see GET /auth/params in authService.ts), which closes off offline bulk
   * precomputation against harvested identifiers.
   */
  async deriveRootKey(password: string, username: string, kdfNonce: string): Promise<Uint8Array> {
    this.cachedKdfNonce = kdfNonce
    const normalisedUsername = username.toLowerCase().trim()

    try {
      const rootKeyBuffer = await dispatchToCryptoWorker<ArrayBuffer>('deriveKey', {
        password,
        username: normalisedUsername,
        kdfNonce,
      })
      return new Uint8Array(rootKeyBuffer)
    } catch (err) {
      // Degrade to the inline, main-thread call only if a worker genuinely
      // can't be spawned in this environment — this will block the UI.
      console.warn('[KeyManager] Falling back to main-thread Argon2id:', err)
      const salt = await sha256Bytes(`${normalisedUsername}:${kdfNonce}`)
      return argon2id({
        password,
        salt,
        ...ARGON2_PARAMS,
      }) as Promise<Uint8Array>
    }
  }

  // ── HKDF splits ────────────────────────────────────────────────────────────

  /**
   * Splits the 512-bit root key into the three functional keys:
   * auth key, master encryption key (MEK), and key encryption key (KEK).
   */
  async deriveKeySet(rootKey: Uint8Array, extractable = true): Promise<KeySet> {
    const [authKeyRaw, masterKeyRaw, kekRaw] = await Promise.all([
      hkdfDerive(rootKey, INFO_AUTH_KEY, 256),
      hkdfDerive(rootKey, INFO_MASTER_KEY, 256),
      hkdfDerive(rootKey, INFO_KEK, 256),
    ])

    const [masterKey, kek] = await Promise.all([
      importAesKey(masterKeyRaw, ['encrypt', 'decrypt'], extractable),
      importAesKey(kekRaw, ['encrypt', 'decrypt'], extractable),
    ])

    // Zero out the raw key bytes as soon as they're imported
    masterKeyRaw.fill(0)
    kekRaw.fill(0)

    return { authKeyRaw, masterKey, kek }
  }

  // ── Registration ───────────────────────────────────────────────────────────

  /**
   * Derives all key material from `password`, `username`, and `kdfNonce` and
   * returns a `RegistrationPayload` ready to POST to /auth/register.
   *
   * Call this ONCE during account creation. The returned payload is sent to the
   * server. The password itself never leaves the client.
   */
  async buildRegistrationPayload(
    password: string,
    username: string,
    kdfNonce: string,
  ): Promise<RegistrationResult> {
    const rootKey = await this.deriveRootKey(password, username, kdfNonce)
    const { authKeyRaw, masterKey, kek } = await this.deriveKeySet(rootKey, true /* extractable — needed to wrap MEK with KEK */)
    rootKey.fill(0)

    // Export the MEK raw bytes so we can encrypt it with the KEK for server backup
    const rawMasterKey = await exportAesKey(masterKey)
    const encryptedMasterKey = await aesGcmEncrypt(kek, rawMasterKey)

    // The auth key is sent to the server which will SHA-256-hash it for storage.
    // We send the raw bytes encoded as base64url.
    const authKeyHash = toBase64Url(authKeyRaw)
    authKeyRaw.fill(0)

    // Independent recovery path: a fresh, high-entropy secret wraps the SAME
    // MEK under a second KEK. The server ends up storing two independently
    // wrapped copies of one key — never anything from which it could derive
    // the MEK on its own — see the "Recovery key" hierarchy at the top of this file.
    const recoveryCodeBytes = crypto.getRandomValues(new Uint8Array(RECOVERY_CODE_BYTES))
    const { recoveryAuthKeyRaw, recoveryKek } = await this.deriveRecoveryKeySet(recoveryCodeBytes, true)
    const recoveryWrappedMasterKey = await aesGcmEncrypt(recoveryKek, rawMasterKey)
    const recoveryAuthKeyHash = toBase64Url(recoveryAuthKeyRaw)
    const recoveryCode = formatRecoveryCode(bytesToBase32(recoveryCodeBytes))

    rawMasterKey.fill(0) // zero the raw bytes immediately
    recoveryCodeBytes.fill(0)
    recoveryAuthKeyRaw.fill(0)

    return {
      payload: {
        authKeyHash,
        encryptedMasterKey,
        recoveryAuthKeyHash,
        recoveryWrappedMasterKey,
      },
      recoveryCode,
    }
  }

  /**
   * Splits a recovery code's raw bytes into its two functional keys: the
   * recovery auth key (proves possession to the server) and the recovery KEK
   * (wraps/unwraps the MEK). Unlike deriveKeySet, this skips Argon2id — the
   * recovery code is already 160 bits of CSPRNG randomness, so a memory-hard
   * KDF would only add latency without adding security.
   */
  async deriveRecoveryKeySet(
    recoveryCodeBytes: Uint8Array,
    extractable = true,
  ): Promise<{ recoveryAuthKeyRaw: Uint8Array; recoveryKek: CryptoKey }> {
    const [recoveryAuthKeyRaw, recoveryKekRaw] = await Promise.all([
      hkdfDerive(recoveryCodeBytes, INFO_RECOVERY_AUTH_KEY, 256),
                                                                   hkdfDerive(recoveryCodeBytes, INFO_RECOVERY_KEK, 256),
    ])
    const recoveryKek = await importAesKey(recoveryKekRaw, ['encrypt', 'decrypt'], extractable)
    recoveryKekRaw.fill(0)
    return { recoveryAuthKeyRaw, recoveryKek }
  }

  /**
   * Generates a replacement recovery code and wraps the EXISTING MEK with it.
   * Nothing from the previous recovery code is needed: replacing the two
   * server-side recovery fields atomically is enough to invalidate it.
   */
  async buildRecoveryRotationPayload(
    existingMasterKey: CryptoKey,
  ): Promise<RecoveryRotationResult> {
    if (!existingMasterKey.extractable) {
      throw new Error('existingMasterKey must be extractable to re-wrap it')
    }

    const recoveryCodeBytes = crypto.getRandomValues(new Uint8Array(RECOVERY_CODE_BYTES))
    const { recoveryAuthKeyRaw, recoveryKek } = await this.deriveRecoveryKeySet(recoveryCodeBytes, true)
    const rawMasterKey = await exportAesKey(existingMasterKey)

    try {
      return {
        recoveryCode: formatRecoveryCode(bytesToBase32(recoveryCodeBytes)),
        recoveryAuthKeyHash: toBase64Url(recoveryAuthKeyRaw),
        recoveryWrappedMasterKey: await aesGcmEncrypt(recoveryKek, rawMasterKey),
      }
    } finally {
      rawMasterKey.fill(0)
      recoveryCodeBytes.fill(0)
      recoveryAuthKeyRaw.fill(0)
    }
  }

  /**
   * Unwraps the MEK using a user-entered recovery code against the
   * server-stored recovery-wrapped MEK blob. Used by the "forgot password"
   * flow. Throws if the code is wrong — the AES-GCM auth tag simply won't verify.
   */
  async unwrapMasterKeyWithRecoveryCode(
    recoveryCodeFormatted: string,
    recoveryWrappedMasterKey: EncryptedBlob,
  ): Promise<CryptoKey> {
    const recoveryCodeBytes = base32ToBytes(unformatRecoveryCode(recoveryCodeFormatted))
    const { recoveryKek } = await this.deriveRecoveryKeySet(recoveryCodeBytes, false)
    recoveryCodeBytes.fill(0)
    const rawMek = await aesGcmDecrypt(recoveryKek, recoveryWrappedMasterKey)
    const masterKey = await importAesKey(rawMek, ['encrypt', 'decrypt'], true)
    rawMek.fill(0)
    return masterKey
  }

  /** Decrypts the server's password-wrapped MEK with the currently derived KEK. */
  async unwrapMasterKeyWithKek(
    kek: CryptoKey,
    encryptedMasterKey: EncryptedBlob | string,
  ): Promise<CryptoKey> {
    const rawMek = await aesGcmDecrypt(kek, normaliseBlob(encryptedMasterKey))
    try {
      return await importAesKey(rawMek, ['encrypt', 'decrypt'], true)
    } finally {
      rawMek.fill(0)
    }
  }

  /**
   * Re-wraps an EXISTING MEK (recovered via unwrapMasterKeyWithRecoveryCode,
   * or pulled back out via getExtractableMasterKey for an authenticated
   * in-session change) under a KEK derived from a freshly chosen password
   * and/or username. This is exactly the password-rotation path the
   * "ARCHITECTURAL REQUIREMENT FOR PASSWORD ROTATION" note above anticipates:
   * it must never generate a new MEK, only re-wrap the one already in use, so
   * existing per-device key caches and the recovery-wrapped copy both stay
   * valid untouched.
   *
   * Shared by three flows that all boil down to "derive a new KEK, re-wrap
   * the same MEK": the recovery-code password reset, an authenticated
   * password change, and an authenticated username change (a username change
   * is cryptographically a full rekey too, since the username feeds the
   * Argon2id salt — see the file header).
   */
  async buildRekeyPayload(
    newPassword: string,
    newUsername: string,
    newKdfNonce: string,
    existingMasterKey: CryptoKey,
  ): Promise<PasswordResetPayload> {
    if (!existingMasterKey.extractable) {
      throw new Error('existingMasterKey must be extractable to re-wrap it')
    }
    const rootKey = await this.deriveRootKey(newPassword, newUsername, newKdfNonce)
    const { authKeyRaw, kek } = await this.deriveKeySet(rootKey, true)
    rootKey.fill(0)

    const rawMasterKey = await exportAesKey(existingMasterKey)
    const encryptedMasterKey = await aesGcmEncrypt(kek, rawMasterKey)
    rawMasterKey.fill(0)

    const authKeyHash = toBase64Url(authKeyRaw)
    authKeyRaw.fill(0)

    return { authKeyHash, encryptedMasterKey }
  }

  // ── Session unlock (login) ─────────────────────────────────────────────────

  /**
   * Re-derives key material from `password` and `email`, then decrypts the
   * server-stored encrypted blobs to recover the MEK.
   *
   * After this call, `getMasterKey()` returns the in-memory keys. They are never
   * written to disk in plaintext.
   *
   * NOTE: This runs Argon2id (slow). If you already have the derived keys in
   * memory from a login flow, use `unlockSessionFromKeys` instead to avoid
   * running Argon2id a second time.
   */
  async unlockSession(
    password: string,
    username: string,
    kdfNonce: string,
    serverPayload: ServerKeyPayload,
    signal?: AbortSignal,
  ): Promise<void> {
    const rootKey = await this.deriveRootKey(password, username, kdfNonce)
    const { authKeyRaw, kek } = await this.deriveKeySet(rootKey, true)
    rootKey.fill(0)
    authKeyRaw.fill(0)
    await this.unlockSessionFromKeys(kek, serverPayload, signal)
  }

  /** Prepare keys without changing the working session or its persisted cache. */
  async prepareSessionFromKeys(kek: CryptoKey, serverPayload: ServerKeyPayload, accountId?: string, kdfNonce?: string, signal?: AbortSignal): Promise<PersistedKeyStore> {
    const masterKey = await this.unwrapMasterKeyWithKek(kek, serverPayload.encryptedMasterKey)
    const raw = await exportAesKey(masterKey)
    try {
      const data: PersistedKeyStore = {
        version: KEY_STORE_SCHEMA_VERSION, accountId, kdfNonce,
        mek: typeof window !== 'undefined' && '__TAURI_INTERNALS__' in window
          ? toBase64Url(raw) : await importAesKey(raw, ['encrypt', 'decrypt'], false),
        canary: await generateKeyCanary(masterKey),
      }
      // Same-account authentication must retain keys for offline documents.
      const existing = await getPlatformBridge()?.readKeyStore?.()
      if (existing && (!existing.accountId || existing.accountId === accountId)) {
        if (await verifyKeyCanary(masterKey, existing.canary)) {
          data.dekCache = existing.dekCache
        } else {
          let oldMaster: CryptoKey | null = null
          try {
            if (existing.mek instanceof CryptoKey) oldMaster = existing.mek
            else {
              const bytes = fromBase64Url(existing.mek)
              try { oldMaster = await importAesKey(bytes, ['encrypt', 'decrypt'], false) } finally { bytes.fill(0) }
            }
          } catch { /* A corrupt cache can be replaced by verified server keys. */ }
          if (oldMaster && await verifyKeyCanary(oldMaster, existing.canary)) {
            throw new Error('The server encryption key does not match this device’s cached writing. Export your local writing before replacing its keys.')
          }
        }
      }
      throwIfAborted(signal)
      return data
    } finally { raw.fill(0) }
  }

  async unlockSessionFromKeys(kek: CryptoKey, serverPayload: ServerKeyPayload, signal?: AbortSignal): Promise<void> {
    const data = await this.prepareSessionFromKeys(kek, serverPayload, undefined, this.cachedKdfNonce ?? undefined, signal)
    await this.persistPreparedKeyStore(data, signal)
    throwIfAborted(signal)
    if (!await this.loadFromKeyStoreData(data, signal)) throw new Error('Unable to unlock saved keys')
  }

  /** Serialize writes so a delayed DEK save cannot overwrite a completed login. */
  async persistPreparedKeyStore(data: PersistedKeyStore | any, signal?: AbortSignal): Promise<void> {
    const task = this.persistQueue.catch(() => {}).then(async () => {
      throwIfAborted(signal)
      const bridge = getPlatformBridge()
      const result = await bridge?.writeKeyStore?.(data)
      if (!result?.success) throw new Error('Encryption keys could not be saved on this device. Free storage space, then try again.')
      const saved = await bridge.readKeyStore()
      await verifyKeyStoreWrite(data, saved, typeof window !== 'undefined' && !('__TAURI_INTERNALS__' in window))
    })
    this.persistQueue = task
    await task
  }

  async pausePersistence(): Promise<void> {
    this.persistencePaused = true
    this.cancelPendingPersistence()
    await this.persistQueue.catch(() => {})
  }

  resumePersistence(): void { this.persistencePaused = false }

  cancelPendingPersistence(): void {
    if (this.persistDebounceTimer !== null) clearTimeout(this.persistDebounceTimer)
    this.persistDebounceTimer = null
  }

  // ── Session persistence & warm restore ───────────────────────────────────

  /**
   * Exports in-memory DEK cache to serializable format for storage.
   */
  async exportDekCache(): Promise<Record<string, { epoch: number; dek: string | CryptoKey }>> {
    const result: Record<string, { epoch: number; dek: string | CryptoKey }> = {}
    for (const [docId, dek] of this.documentKeys.entries()) {
      const epoch = this.documentEpochs.get(docId) ?? 1
      try {
        if (dek.extractable) {
          const raw = await exportAesKey(dek)
          result[docId] = { epoch, dek: toBase64Url(raw) }
          raw.fill(0)
        } else {
          result[docId] = { epoch, dek }
        }
      } catch {
        result[docId] = { epoch, dek }
      }
    }
    return result
  }

  /**
   * Persists the current in-memory key state to the local keystore via platform bridge.
   * Accepts optional raw keys (if available in Uint8Array form before zeroing) to avoid re-exporting.
   */
  async saveToLocalCache(rawMek?: Uint8Array, signal?: AbortSignal): Promise<boolean> {
    if (!this.masterKey || this.persistencePaused) return false
    try {
      const bridge = getPlatformBridge()
      if (!bridge || typeof (bridge as any).writeKeyStore !== 'function') {
        return false
      }

      let mekPayload: string | CryptoKey = this.masterKey
      let canaryBlob: EncryptedBlob | null = null
      let offlineUnlock = this.offlineUnlock

      if (rawMek) {
        mekPayload = toBase64Url(rawMek)
      }

      // If raw keys weren't provided, read existing keystore to retain base64 strings if on native
      if (typeof mekPayload !== 'string') {
        if (typeof (bridge as any).readKeyStore === 'function') {
          const existing = await (bridge as any).readKeyStore()
          if (existing) {
            if ((existing.accountId && this.accountId && existing.accountId !== this.accountId) || !await verifyKeyCanary(this.masterKey, existing.canary)) {
              throw new Error('Persisted keys do not match the active account')
            }
            if (typeof existing.mek === 'string' && typeof mekPayload !== 'string') {
              mekPayload = existing.mek
            }
            if (existing.offlineUnlock) offlineUnlock = existing.offlineUnlock
            if (existing.canary) {
              canaryBlob = existing.canary
            }
          }
        }
      }

      // If we still don't have canaryBlob, generate fresh one under current masterKey
      if (!canaryBlob) {
        canaryBlob = await generateKeyCanary(this.masterKey)
      }

      const dekCache = await this.exportDekCache()

      const data: any = {
        version: KEY_STORE_SCHEMA_VERSION,
        accountId: this.accountId ?? undefined,
        offlineUnlock,
        mek: mekPayload,
        dekCache: Object.keys(dekCache).length > 0 ? dekCache : undefined,
        canary: canaryBlob,
        kdfNonce: offlineUnlock?.kdfNonce ?? await secureStorage.getKdfNonce() ?? this.cachedKdfNonce ?? undefined,
      }

      throwIfAborted(signal)
      if (this.persistencePaused) return false
      await this.persistPreparedKeyStore(data, signal)
      return true
    } catch (err) {
      console.warn('[KeyManager] Failed to save keys to local cache:', err)
      return false
    }
  }

  // ── Session warm restore (local cache bypass) ─────────────────────────────

  /**
   * Directly sets the in-memory keys without running Argon2id or decrypting server payloads.
   * Used by warm restore paths (native local key store, IndexedDB on web).
   */
  restoreSessionDirect(
    masterKey: CryptoKey,
    documentKeys?: Map<string, CryptoKey> | Record<string, CryptoKey>,
  ): void {
    this.masterKey = masterKey
    if (documentKeys) {
      if (documentKeys instanceof Map) {
        this.documentKeys = new Map(documentKeys)
      } else {
        this.documentKeys = new Map(Object.entries(documentKeys))
      }
    }
    this.notifyUnlockChange()
  }

  /**
   * Helper to verify canary and populate key state from a PersistedKeyStore or WebKeyStore.
   */
  async loadFromKeyStoreData(data: PersistedKeyStore | any, signal?: AbortSignal, expectedAccountId?: string): Promise<boolean> {
    if (expectedAccountId && data?.accountId && data.accountId !== expectedAccountId) return false
    if (!data || typeof data !== 'object') {
      console.warn('[KeyManager] Warm restore rejected: keystore data is missing or not an object')
      return false
    }

    // Verify version
    if (typeof data.version !== 'number' || data.version > KEY_STORE_SCHEMA_VERSION) {
      console.warn(`[KeyManager] Warm restore rejected: unsupported schema version (${data.version})`)
      return false
    }

    // Canary is required for integrity verification
    if (!data.canary || typeof data.canary !== 'object') {
      console.warn('[KeyManager] Warm restore rejected: canary missing')
      return false
    }

    try {
      let masterKey: CryptoKey
      const docKeys = new Map<string, CryptoKey>()

      // Check if CryptoKey objects directly (e.g. from IndexedDB on web) or serialized strings (native)
      if (data.mek instanceof CryptoKey) {
        masterKey = data.mek
      } else if (typeof data.mek === 'string') {
        const rawMek = fromBase64Url(data.mek)
        masterKey = await importAesKey(rawMek, ['encrypt', 'decrypt'], false)
        rawMek.fill(0)
      } else {
        console.warn('[KeyManager] Warm restore rejected: invalid MEK format')
        return false
      }

      // Verify canary integrity (AES-GCM authentication tag check)
      const isCanaryValid = await verifyKeyCanary(masterKey, data.canary)
      if (!isCanaryValid) {
        console.warn('[KeyManager] Canary verification failed: corrupted keystore or invalid key')
        return false
      }

      // Restore DEK cache
      if (data.dekCache && typeof data.dekCache === 'object') {
        for (const [docId, entry] of Object.entries(data.dekCache)) {
          if (!entry || typeof entry !== 'object') continue
          const entryObj = entry as { dek: string | CryptoKey; epoch?: number }
          if (entryObj.dek instanceof CryptoKey) {
            docKeys.set(docId, entryObj.dek)
          } else if (typeof entryObj.dek === 'string') {
            const rawDek = fromBase64Url(entryObj.dek)
            const dekKey = await importAesKey(rawDek, ['encrypt', 'decrypt'], true)
            rawDek.fill(0)
            docKeys.set(docId, dekKey)
          }
        }
      }

      throwIfAborted(signal)
      const retainedEpochs = new Map<string, number>()
      if (this.masterKey && (!this.accountId || this.accountId === (data.accountId ?? expectedAccountId)) && await verifyKeyCanary(this.masterKey, data.canary)) {
        for (const [id, key] of this.documentKeys) {
          const currentEpoch = this.documentEpochs.get(id) ?? 1
          const savedEpoch = data.dekCache?.[id]?.epoch ?? 0
          if (currentEpoch >= savedEpoch) {
            docKeys.set(id, key)
            retainedEpochs.set(id, currentEpoch)
          }
        }
      }
      throwIfAborted(signal)
      this.documentEpochs.clear()
      for (const [id, entry] of Object.entries(data.dekCache ?? {})) {
        const epoch = (entry as PersistedDekEntry).epoch
        if (typeof epoch === 'number') this.documentEpochs.set(id, epoch)
      }
      for (const [id, epoch] of retainedEpochs) this.documentEpochs.set(id, epoch)
      this.offlineUnlock = data.offlineUnlock
      this.cachedKdfNonce = typeof data.kdfNonce === 'string' ? data.kdfNonce : null
      this.accountId = data.accountId ?? expectedAccountId ?? null
      this.restoreSessionDirect(masterKey, docKeys)
      return true
    } catch (err) {
      console.warn('[KeyManager] Failed to load key store data:', err)
      return false
    }
  }

  /**
   * Warm restore: reads the local persisted key store, verifies integrity
   * (canary check), and populates in-memory key state directly.
   *
   * Bypasses Argon2id derivation, network calls, and password prompts.
   * Returns true on successful restore, false if cache is absent or invalid.
   */
  async updateOfflineUnlock(proof: NonNullable<PersistedKeyStore['offlineUnlock']>): Promise<void> {
    await this.pausePersistence()
    try {
      const data = await getPlatformBridge()?.readKeyStore?.()
      if (!data || !this.masterKey || !await verifyKeyCanary(this.masterKey, data.canary)) throw new Error('Local keys are unavailable')
      await this.persistPreparedKeyStore({ ...data, dekCache: { ...data.dekCache, ...await this.exportDekCache() }, offlineUnlock: proof, kdfNonce: proof.kdfNonce })
      this.offlineUnlock = proof
      this.cachedKdfNonce = proof.kdfNonce
    } finally { this.resumePersistence() }
  }

  async unlockFromLocalCache(password: string, accountId: string, signal?: AbortSignal): Promise<boolean> {
    const bridge = getPlatformBridge()
    const primary = await bridge?.readKeyStore?.().catch(() => null)
    const backup = await bridge?.readKeyStoreBackup?.().catch(() => null)
    for (const data of [primary, backup]) {
      const candidate = new KeyManager()
      if (!await candidate.loadFromKeyStoreData(data, signal, accountId)) continue
      candidate.clear()
      // A readable primary is authoritative: never accept an older recovery
      // password simply because the current password failed.
      if (!data?.offlineUnlock || data.accountId !== accountId) return false
      const proof = data.offlineUnlock
      const root = await candidate.deriveRootKey(password, proof.username, proof.kdfNonce)
      const { authKeyRaw, kek } = await candidate.deriveKeySet(root, false)
      root.fill(0)
      authKeyRaw.fill(0)
      throwIfAborted(signal)
      try {
        const master = await candidate.unwrapMasterKeyWithKek(kek, proof.encryptedMasterKey)
        if (!await verifyKeyCanary(master, data.canary)) return false
        return await this.loadFromKeyStoreData(data, signal, accountId)
      } catch { return false }
    }
    throwIfAborted(signal)
    return false
  }

  async restoreFromLocalCache(signal?: AbortSignal, expectedAccountId?: string): Promise<boolean> {
    try {
      const bridge = getPlatformBridge()
      if (!bridge || typeof (bridge as any).readKeyStore !== 'function') {
        return false
      }
      const data = await bridge.readKeyStore().catch(() => null)
      throwIfAborted(signal)
      if (await this.loadFromKeyStoreData(data, signal, expectedAccountId)) return true
      throwIfAborted(signal)
      const backup = await bridge.readKeyStoreBackup?.()
      if (!backup || !await this.loadFromKeyStoreData(backup, signal, expectedAccountId)) return false
      // A readable verified backup grants local use even when storage is too
      // full to repair the primary immediately.
      try { await this.persistPreparedKeyStore(backup, signal) } catch {
        throwIfAborted(signal)
        if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent('auth:storage-error'))
      }
      throwIfAborted(signal)
      return true
    } catch (err) {
      console.warn('[KeyManager] restoreFromLocalCache failed:', err)
      return false
    }
  }

  // ── Document DEK management ────────────────────────────────────────────────

  /**
   * Generates a new DEK for a document, wraps it with the MEK, and caches it.
   * Returns the wrapped DEK (an `EncryptedBlob`) to be stored in D1.
   */
  async generateAndWrapDek(docId: string, epoch?: number): Promise<EncryptedBlob> {
    if (!this.masterKey) throw new Error('Session not unlocked — call unlockSession first')
    const dek = await generateAesKey()
    const rawDek = await exportAesKey(dek)
    const wrapped = await aesGcmEncrypt(this.masterKey, rawDek)
    rawDek.fill(0)
    this.documentKeys.set(docId, dek)
    if (epoch !== undefined) {
      this.documentEpochs.set(docId, epoch)
    }
    this.schedulePersist()
    return wrapped
  }

  /**
   * Reuses the existing DEK if cached, or generates a new one, wraps it, and caches it.
   */
  async getOrGenerateAndWrapDek(docId: string, epoch?: number): Promise<EncryptedBlob> {
    if (!this.masterKey) throw new Error('Session not unlocked — call unlockSession first')
    let dek = this.documentKeys.get(docId)
    let isNew = false
    if (!dek) {
      dek = await generateAesKey()
      this.documentKeys.set(docId, dek)
      isNew = true
    }
    if (epoch !== undefined) {
      this.documentEpochs.set(docId, epoch)
    }
    const rawDek = await exportAesKey(dek)
    const wrapped = await aesGcmEncrypt(this.masterKey, rawDek)
    rawDek.fill(0)
    if (isNew) {
      this.schedulePersist()
    }
    return wrapped
  }

  /**
   * Unwraps a server-stored wrapped DEK and caches it for `docId`.
   * Call this after fetching the wrapped DEK from `/api/documents/:id/keys`.
   */
  async unwrapAndCacheDek(docId: string, wrappedDek: EncryptedBlob, epoch?: number): Promise<CryptoKey> {
    if (!this.masterKey) throw new Error('Session not unlocked — call unlockSession first')
    const rawDek = await aesGcmDecrypt(this.masterKey, wrappedDek)
    const dek = await importAesKey(rawDek, ['encrypt', 'decrypt'], true)
    rawDek.fill(0)
    this.documentKeys.set(docId, dek)
    if (epoch !== undefined) {
      this.documentEpochs.set(docId, epoch)
    }
    this.schedulePersist()
    return dek
  }

  // ── Accessors ──────────────────────────────────────────────────────────────

  getMasterKey(): CryptoKey | null {
    return this.masterKey
  }

  /**
   * Returns the kdf_nonce last used to derive the root key, if any.
   * Used by the auth store to mirror the nonce into secureStorage so it's
   * available for a future local re-derivation ("lock" feature) without
   * needing a network round-trip.
   */
  getCachedKdfNonce(): string | null {
    return this.cachedKdfNonce
  }

  /**
   * Retrieves cached DEK for a document.
   *
   * Note on sharing revocation:
   * A revoked collaborator's already-synced device retains local decrypt capability
   * for DEKs cached prior to revocation. This is a known, tracked gap tied to a
   * planned rework of the sharing/revocation architecture.
   */
  getDocumentKey(docId: string): CryptoKey | undefined {
    return this.documentKeys.get(docId)
  }

  /**
   * Checks whether an unwrapped DEK is already cached in memory for a document.
   */
  hasDocumentKey(docId: string): boolean {
    return this.documentKeys.has(docId)
  }

  getDocumentEpoch(docId: string): number | undefined {
    return this.documentEpochs.get(docId)
  }

  getAllDocumentKeys(): Map<string, CryptoKey> {
    return new Map(this.documentKeys)
  }

  setDocumentKey(docId: string, dek: CryptoKey, epoch?: number): void {
    this.documentKeys.set(docId, dek)
    if (epoch !== undefined) {
      this.documentEpochs.set(docId, epoch)
    }
    this.schedulePersist()
  }

  setMasterKey(key: CryptoKey | null): void {
    this.masterKey = key
    this.notifyUnlockChange()
  }

  isUnlocked(): boolean {
    return this.masterKey !== null
  }

  // ── Teardown ───────────────────────────────────────────────────────────────

  /**
   * Drops all in-memory key material. Call on logout.
   */
  clear(): void {
    this.offlineUnlock = undefined
    this.accountId = null
    if (this.persistDebounceTimer !== null) {
      clearTimeout(this.persistDebounceTimer)
      this.persistDebounceTimer = null
    }
    this.masterKey = null
    this.cachedKdfNonce = null
    this.documentKeys.clear()
    this.documentEpochs.clear()
    this.notifyUnlockChange()
  }

  /**
   * Deletes the persisted local keystore from disk / IndexedDB.
   */
  async clearLocalCache(): Promise<void> {
    this.cancelPendingPersistence()
    await this.persistQueue.catch(() => {})
    const result = await getPlatformBridge()?.deleteKeyStore?.()
    if (!result?.success) throw new Error('Encryption keys could not be removed from this device')
  }
}

// ── Singleton ─────────────────────────────────────────────────────────────────

export const keyManager = new KeyManager()

// ── Internal helpers ──────────────────────────────────────────────────────────

/**
 * Accepts either an `EncryptedBlob` object or a legacy server response
 * where iv and ciphertext were sent as separate top-level fields.
 */
function normaliseBlob(value: EncryptedBlob | string | unknown): EncryptedBlob {
  if (value && typeof value === 'object' && 'iv' in (value as object) && 'ciphertext' in (value as object)) {
    return value as EncryptedBlob
  }
  throw new Error(
    'Cannot normalize encrypted blob — unexpected format from server: ' + JSON.stringify(value),
  )
}
